import React from 'react';
import { FileText, AlertCircle, Calendar, CreditCard, Shield, Baby, Heart } from 'lucide-react';
import SEOHead from '../components/SEOHead';

const CGV: React.FC = () => {
  return (
    <article className="pt-24 pb-16 bg-neutral-offwhite min-h-screen">
      <SEOHead
        title="Conditions Générales de Vente"
        description="Conditions générales de vente de Doux ce Moman : réservation, paiement, annulation, report, bons cadeaux, conditions de santé et droit de rétractation."
        path="/cgv"
        noindex
      />
      <div className="container mx-auto px-4">
        {/* Hero Section */}
        <header className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-brand/10 rounded-full mb-6">
            <FileText className="text-brand" size={32} />
          </div>
          <h1 className="font-playfair text-4xl md:text-5xl font-bold text-neutral-ink mb-4">
            Conditions Générales de Vente
          </h1>
          <p className="font-nunito text-neutral-body max-w-2xl mx-auto">
            Dernière mise à jour : Octobre 2025
          </p>
        </header>

        {/* Content */}
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg p-8 md:p-12 space-y-8">
          
          {/* Article 1 */}
          <section>
            <h2 className="font-playfair text-2xl font-bold text-neutral-ink mb-4 flex items-center">
              <FileText className="mr-3 text-brand" size={24} />
              Article 1 : Objet et Champ d'Application
            </h2>
            <p className="font-nunito text-neutral-body leading-relaxed">
              Les présentes Conditions Générales de Vente (CGV) régissent toutes les prestations de services et d'ateliers proposées par Doux ce Moman (massages prénataux, bains enveloppés, baby spa, etc.) et s'appliquent sans restriction ni réserve à l'ensemble de la clientèle. La réservation d'une prestation implique l'acceptation pleine et entière des présentes CGV.
            </p>
          </section>

          {/* Article 2 */}
          <section>
            <h2 className="font-playfair text-2xl font-bold text-neutral-ink mb-4 flex items-center">
              <CreditCard className="mr-3 text-brand" size={24} />
              Article 2 : Réservation et Modalités de Paiement
            </h2>
            <div className="space-y-4 font-nunito text-neutral-body">
              <div>
                <h3 className="font-semibold text-neutral-ink mb-2">Prise de Rendez-vous</h3>
                <p className="leading-relaxed">
                  Toute prestation se fait uniquement sur rendez-vous, et peut être effectuée par téléphone ou en ligne via notre plateforme de réservation.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-neutral-ink mb-2">Règlement</h3>
                <p className="leading-relaxed">
                  Tous les soins nécessitent un règlement total à la réservation. Le règlement des prestations s'effectue par carte bancaire.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-neutral-ink mb-2">Bons Cadeaux</h3>
                <ul className="list-disc list-inside space-y-1 leading-relaxed ml-4">
                  <li>Les Bons Cadeaux sont valables 9 mois à partir de leur date d'achat.</li>
                  <li>Ils sont non remboursables et non échangeables contre de l'argent.</li>
                  <li>Ils ne pourront pas être utilisés après leur date d'expiration.</li>
                  <li>En cas de perte ou de vol, aucun remplacement ne sera possible.</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Article 3 */}
          <section>
            <h2 className="font-playfair text-2xl font-bold text-neutral-ink mb-4 flex items-center">
              <Calendar className="mr-3 text-brand" size={24} />
              Article 3 : Annulation, Report et Remboursement
            </h2>
            <div className="space-y-4 font-nunito text-neutral-body">
              <div>
                <h3 className="font-semibold text-neutral-ink mb-2">Report par le Client</h3>
                <p className="leading-relaxed">
                  Tout report de rendez-vous doit être signalé par le client au minimum 48 heures à l'avance. Le soin peut être reporté une seule fois, dans un délai de 3 mois maximum à compter de la date initiale, sous réserve de disponibilité.
                </p>
              </div>
              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
                <h3 className="font-semibold text-red-800 mb-2 flex items-center">
                  <AlertCircle className="mr-2" size={18} />
                  Annulation Tardive ou Absence (No Show)
                </h3>
                <p className="text-red-700 leading-relaxed">
                  En cas d'annulation tardive (moins de 48h) ou d'absence non justifiée, la prestation sera considérée comme due et aucun report ne sera accordé.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-neutral-ink mb-2">Annulation Médicale</h3>
                <p className="leading-relaxed mb-2">
                  En cas d'annulation pour raison médicale (pour la mère ou le bébé), un certificat médical est obligatoire. Cette annulation donnera lieu à un report ou à un avoir, selon la nature de la prestation et ses contraintes :
                </p>
                <ul className="list-disc list-inside space-y-2 leading-relaxed ml-4">
                  <li>
                    <strong>Pour le Bain Enveloppé (limité à 6 semaines) et le Massage Prénatal (limité à la grossesse) :</strong> Si le report de la séance n'est plus possible en raison de la limite d'âge du bébé ou de la fin de l'état de grossesse de la mère, la somme versée ne sera pas remboursée. Elle donnera lieu à l'émission d'un avoir, valable 6 mois à compter de la date d'annulation, utilisable sur l'ensemble des prestations proposées par Doux ce Moman.
                  </li>
                  <li>
                    <strong>Pour les autres prestations (Spa Bébé, Ateliers, etc.) :</strong> La séance pourra être reprogrammée dans un délai de 3 mois maximum. Passé ce délai, la prestation sera considérée comme due et aucun avoir ou report ne sera possible.
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-neutral-ink mb-2">Remboursement</h3>
                <p className="leading-relaxed">
                  Aucun remboursement ne sera effectué, quelle que soit la raison. Un soin réglé et non effectué peut être reporté selon les conditions ci-dessus, mais en aucun cas remboursé.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-neutral-ink mb-2">Annulation par Doux ce Moman</h3>
                <p className="leading-relaxed">
                  En cas d'annulation de la séance par Doux ce Moman (force majeure, maladie, etc.), le client sera prévenu dans les plus brefs délais et la prestation sera intégralement reportée. Si le report s'avère impossible ou si le client le souhaite, un remboursement intégral sera effectué.
                </p>
              </div>
            </div>
          </section>

          {/* Article 4 */}
          <section>
            <h2 className="font-playfair text-2xl font-bold text-neutral-ink mb-4 flex items-center">
              <AlertCircle className="mr-3 text-brand" size={24} />
              Article 4 : Retard
            </h2>
            <p className="font-nunito text-neutral-body leading-relaxed">
              En cas de retard du client, la durée du soin sera obligatoirement réduite afin de ne pas impacter le déroulement des rendez-vous suivants. Le tarif de la prestation initialement réservée reste inchangé et intégralement dû.
            </p>
          </section>

          {/* Article 5 */}
          <section>
            <h2 className="font-playfair text-2xl font-bold text-neutral-ink mb-4 flex items-center">
              <Shield className="mr-3 text-brand" size={24} />
              Article 5 : Conditions de Santé et Responsabilité
            </h2>
            <div className="space-y-4 font-nunito text-neutral-body">
              <div>
                <h3 className="font-semibold text-neutral-ink mb-2">Engagement Client</h3>
                <p className="leading-relaxed">
                  Le client s'engage à informer Doux ce Moman de tout problème de santé, traitement médical ou allergie en remplissant le questionnaire de santé qui lui sera remis.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-neutral-ink mb-2">Responsabilité</h3>
                <p className="leading-relaxed">
                  Doux ce Moman décline toute responsabilité en cas d'omission ou de fausse déclaration de ces informations.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-neutral-ink mb-2">Femmes Enceintes</h3>
                <p className="leading-relaxed">
                  Les soins pour femmes enceintes sont réalisés à partir du 4ᵉ mois jusqu'à la fin de la grossesse. Il est obligatoire d'indiquer votre grossesse à la réservation. Certains soins peuvent être refusés sans avis médical préalable.
                </p>
              </div>
            </div>
          </section>

          {/* Article 6 */}
          <section>
            <h2 className="font-playfair text-2xl font-bold text-neutral-ink mb-4 flex items-center">
              <Baby className="mr-3 text-brand" size={24} />
              Article 6 : Conditions Spécifiques aux Soins Bébés et Mineurs
            </h2>
            <div className="space-y-4 font-nunito text-neutral-body">
              <div>
                <h3 className="font-semibold text-neutral-ink mb-2">Soins Bébés / Baby Spa</h3>
                <ul className="list-disc list-inside space-y-1 leading-relaxed ml-4">
                  <li>Les soins pour bébés sont adaptés aux tout-petits, dès 3 mois (ou selon les conditions spécifiques du Bain Enveloppé pour les nouveau-nés).</li>
                  <li>Ils sont pratiqués dans un cadre de bien-être et de relaxation uniquement (hydrothérapie, massage bébé) et n'ont aucun but médical ou thérapeutique.</li>
                  <li>La présence d'un parent est obligatoire pendant toute la durée du soin, sans exception.</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-neutral-ink mb-2">Santé du Bébé</h3>
                <p className="leading-relaxed">
                  Les parents doivent informer le centre de tout problème de santé ou antécédent médical du bébé avant le soin. Aucun soin ne sera pratiqué sur un bébé malade, fiévreux ou présentant des symptômes suspects. Le centre se réserve le droit de refuser ou reporter un soin si l'état de santé du bébé semble incompatible avec la prestation prévue. Un accord du pédiatre peut être demandé dans certains cas.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-neutral-ink mb-2">Soins Mineurs (Kid's Spa)</h3>
                <ul className="list-disc list-inside space-y-1 leading-relaxed ml-4">
                  <li>Soins proposés à partir de 18 mois.</li>
                  <li>La présence d'un parent est obligatoire pendant toute la durée du soin, sans exception.</li>
                  <li>Les soins sont à visée ludique et bien-être uniquement (pas de manipulation thérapeutique).</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Article 7 */}
          <section>
            <h2 className="font-playfair text-2xl font-bold text-neutral-ink mb-4 flex items-center">
              <Heart className="mr-3 text-brand" size={24} />
              Article 7 : Respect et Hygiène du Centre
            </h2>
            <p className="font-nunito text-neutral-body leading-relaxed">
              Nous demandons à toute notre clientèle de faire preuve d'une attitude respectueuse envers le personnel et les autres clients, ainsi que de maintenir une hygiène irréprochable au sein du local. Toute attitude déplacée entraînera l'annulation immédiate du soin, sans remboursement.
            </p>
          </section>

          {/* Article 8 */}
          <section>
            <h2 className="font-playfair text-2xl font-bold text-neutral-ink mb-4 flex items-center">
              <Shield className="mr-3 text-brand" size={24} />
              Article 8 : Droit Applicable et Règlement des Litiges
            </h2>
            <p className="font-nunito text-neutral-body leading-relaxed">
              Les présentes CGV sont régies par le droit français. En cas de litige, les parties s'engagent à tenter une résolution amiable avant toute action en justice.
            </p>
          </section>

          {/* Article 9 */}
          <section>
            <h2 className="font-playfair text-2xl font-bold text-neutral-ink mb-4 flex items-center">
              <AlertCircle className="mr-3 text-brand" size={24} />
              Article 9 : Droit de Rétractation
            </h2>
            <div className="space-y-4 font-nunito text-neutral-body">
              <div>
                <h3 className="font-semibold text-neutral-ink mb-2">Délai Légal (achats à distance)</h3>
                <p className="leading-relaxed">
                  Conformément à l'article L. 221-18 du Code de la consommation, pour toute commande de Bons Cadeaux ou de prestations de services réglées à distance (en ligne ou par téléphone), le client dispose d'un délai de rétractation de quatorze (14) jours calendaires à compter de la date de la commande ou de l'achat du Bon Cadeau, sans avoir à justifier de motif ni à supporter de pénalités.
                </p>
              </div>
              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded">
                <h3 className="font-semibold text-yellow-800 mb-2">Exceptions (Prestations à date déterminée)</h3>
                <p className="text-yellow-700 leading-relaxed mb-2">
                  Conformément à l'article L. 221-28 du Code de la consommation, le droit de rétractation ne peut être exercé pour les contrats de prestations de services d'activités de loisirs qui doivent être fournis à une date ou à une périodicité déterminée. Par conséquent :
                </p>
                <ul className="list-disc list-inside space-y-1 text-yellow-700 leading-relaxed ml-4">
                  <li>Toute réservation d'une séance ou d'un atelier à une date et heure spécifique n'est pas soumise au droit de rétractation.</li>
                  <li>Si le client souhaite que la prestation de service commence avant la fin du délai de rétractation de 14 jours (pour une prestation à date non déterminée, comme un Bon Cadeau), il doit donner son accord exprès et renonce alors à son droit de rétractation une fois la prestation intégralement exécutée.</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-neutral-ink mb-2">Modalités d'Exercice</h3>
                <p className="leading-relaxed mb-2">
                  Pour exercer son droit de rétractation, le Client doit notifier sa décision à Doux ce Moman au moyen d'une déclaration dénuée d'ambiguïté, exprimant sa volonté de se rétracter.
                </p>
                <p className="leading-relaxed mb-2">
                  Cette déclaration doit être adressée avant l'expiration du délai de quatorze (14) jours :
                </p>
                <ul className="list-disc list-inside space-y-2 leading-relaxed ml-4">
                  <li>
                    <strong>Par courrier postal</strong> (de préférence par lettre recommandée avec accusé de réception, afin de disposer d'une preuve de la date d'envoi) à l'adresse suivante :<br />
                    <span className="text-brand font-semibold">Doux ce Moman - 32 rue Lyautey 25870 Geneuille</span>
                  </li>
                  <li>
                    <strong>Ou par courrier électronique (e-mail)</strong> à l'adresse :<br />
                    <a href="mailto:contact@douxcemoman.fr" className="text-brand font-semibold hover:underline">contact@douxcemoman.fr</a>
                  </li>
                </ul>
                <p className="leading-relaxed mt-2">
                  En cas d'exercice du droit de rétractation, Doux ce Moman remboursera le client de la totalité des sommes versées dans un délai de quatorze (14) jours à compter de la réception de la décision de rétractation, en utilisant le même moyen de paiement que celui utilisé pour la transaction initiale.
                </p>
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="border-t pt-8">
            <div className="bg-brand/5 rounded-xl p-6">
              <h3 className="font-playfair text-xl font-bold text-neutral-ink mb-4">
                Questions sur nos CGV ?
              </h3>
              <p className="font-nunito text-neutral-body mb-4">
                Pour toute question concernant nos conditions générales de vente, n'hésitez pas à nous contacter :
              </p>
              <div className="space-y-2 font-nunito text-neutral-body">
                <p><strong>Email :</strong> <a href="mailto:contact@douxcemoman.fr" className="text-brand hover:underline">contact@douxcemoman.fr</a></p>
                <p><strong>Téléphone :</strong> <a href="tel:+33123456789" className="text-brand hover:underline">01 23 45 67 89</a></p>
                <p><strong>Adresse :</strong> 32 rue Lyautey, 25870 Geneuille</p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </article>
  );
};

export default CGV;
